// src/components/laboratory/forms/PromotionForm.jsx
import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_PROMOTION,
  UPDATE_PROMOTION,
  GET_PRODUCTS_BY_LABORATORY,
  GET_CONTRACTS_BY_LABORATORY,
  GET_PROMOTIONS_BY_LABORATORY
} from '../../../api/laboratory-api';
import ModalComponent from '../../common/ModalComponent';
import { Info, Calendar } from 'lucide-react';
import { format, addMonths } from 'date-fns';
import { toast } from 'react-toastify';

const PromotionForm = ({ laboratory, promotion, isOpen, onClose, onSuccess }) => {
  const USER_TYPES = [
    { value: 'SELLER', label: 'Vendeur (SELLER)' },
    { value: 'PHARMACIST', label: 'Pharmacien (PHARMACIST)' },
    { value: 'PARAPHARMACIST', label: 'Parapharmacien (PARAPHARMACIST)' },
  ];

  const [formData, setFormData] = useState({
    name: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
    quantity: '100',
    sellerCommissionDH: '',   // Commission vendeur en DH (fixe)
    mazidCommissionPct: '',   // Commission Mazid en % du PGHT
    contratId: '',
    productIds: [],
    allowedUserTypes: [],
    pdfFile: null,
    pdfFileName: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableContracts, setAvailableContracts] = useState([]);

  // Fetch products
  const { data: productsData } = useQuery(GET_PRODUCTS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id },
    skip: !laboratory?.id,
    fetchPolicy: 'network-only'
  });

  // Fetch contracts
  const { data: contractsData } = useQuery(GET_CONTRACTS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id },
    skip: !laboratory?.id,
    fetchPolicy: 'network-only'
  });

  // Fetch existing promotions for overlap check
  const { data: existingPromosData } = useQuery(GET_PROMOTIONS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id },
    skip: !laboratory?.id,
    fetchPolicy: 'network-only'
  });

  // Set available products and contracts when data is loaded
  useEffect(() => {
    if (productsData?.productsByLaboratory) {
      setAvailableProducts(productsData.productsByLaboratory);
    }
    if (contractsData?.contractsByLaboratory) {
      setAvailableContracts(contractsData.contractsByLaboratory);
    }
  }, [productsData, contractsData]);

  // Initialize form data if editing an existing promotion
  useEffect(() => {
    if (promotion) {
      // Extract product IDs from the promotion
      const productIds = promotion.products 
        ? promotion.products.map(product => product.id || product.productId)
        : [];

      setFormData({
        name: promotion.name || '',
        startDate: format(new Date(promotion.startDate), 'yyyy-MM-dd'),
        endDate: format(new Date(promotion.endDate), 'yyyy-MM-dd'),
        quantity: promotion.quantity ? String(promotion.quantity) : '100',
        // commissionAmount = seller DH, commissionPercentage = Mazid %
        sellerCommissionDH: String(promotion.commissionAmount || ''),
        mazidCommissionPct: String(promotion.commissionPercentage || ''),
        contratId: promotion.contratId || '',
        productIds,
        allowedUserTypes: promotion.allowedUserTypes || [],
        pdfFile: null,
        pdfFileName: promotion.pdfFile || ''
      });
    } else {
      // Reset form for a new promotion
      setFormData({
        name: '',
        startDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
        quantity: '100',
        sellerCommissionDH: '',
        mazidCommissionPct: '',
        contratId: '',
        productIds: [],
        allowedUserTypes: [],
        pdfFile: null,
        pdfFileName: ''
      });
    }
    setFormErrors({});
  }, [promotion, isOpen]);

  // Mutations
  const [createPromotion] = useMutation(CREATE_PROMOTION, {
    onCompleted: (data) => {
      if (data.createPromotion) {
        toast.success('Promotion créée avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la création de la promotion');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la création de la promotion: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const [updatePromotion] = useMutation(UPDATE_PROMOTION, {
    onCompleted: (data) => {
      if (data.updatePromotion) {
        toast.success('Promotion mise à jour avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la mise à jour de la promotion');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour de la promotion: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Compute Mazid gain in DH for selected products (based on PGHT)
  const getMazidGainDH = () => {
    const pct = parseFloat(formData.mazidCommissionPct);
    if (!pct || formData.productIds.length === 0) return null;
    const selected = availableProducts.filter(p => formData.productIds.includes(p.id));
    if (selected.length === 0) return null;
    const avgPGHT = selected.reduce((s, p) => s + (p.prixPGHT || p.price || 0), 0) / selected.length;
    return (avgPGHT * pct / 100).toFixed(2);
  };

  // Product selection handler
  const handleProductChange = (e) => {
    const { value, checked } = e.target;

    setFormData(prev => {
      const updatedProductIds = checked
        ? [...prev.productIds, value]
        : prev.productIds.filter(id => id !== value);

      return {
        ...prev,
        productIds: updatedProductIds
      };
    });

    // Clear error if at least one product is selected
    if (checked && formErrors.productIds) {
      setFormErrors(prev => ({ ...prev, productIds: undefined }));
    }
  };

  // File upload handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        pdfFile: file,
        pdfFileName: file.name
      }));
    }
  };

  // User type selection handler
  const handleUserTypeChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      allowedUserTypes: checked
        ? [...prev.allowedUserTypes, value]
        : prev.allowedUserTypes.filter(t => t !== value),
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Le nom de la promotion est requis';
    }

    if (!formData.startDate) {
      errors.startDate = 'La date de début est requise';
    }

    if (!formData.endDate) {
      errors.endDate = 'La date de fin est requise';
    } else if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      errors.endDate = 'La date de fin doit être postérieure à la date de début';
    }

    // Overlap check: no other promotion on this lab can overlap this period
    if (formData.startDate && formData.endDate) {
      const newStart = new Date(formData.startDate);
      const newEnd = new Date(formData.endDate);
      const existingPromos = existingPromosData?.promotionsByLaboratory || [];
      const overlapping = existingPromos.find(p => {
        if (promotion && p.id === promotion.id) return false; // skip self when editing
        const pStart = new Date(p.startDate);
        const pEnd = new Date(p.endDate);
        return newStart <= pEnd && newEnd >= pStart;
      });
      if (overlapping) {
        errors.startDate = `Conflit avec la promotion "${overlapping.name}" (${format(new Date(overlapping.startDate), 'dd/MM/yyyy')} – ${format(new Date(overlapping.endDate), 'dd/MM/yyyy')}). Choisissez une autre période.`;
      }
    }

    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      errors.quantity = 'La quantité doit être un nombre positif';
    }

    if (!formData.sellerCommissionDH || parseFloat(formData.sellerCommissionDH) < 0) {
      errors.sellerCommissionDH = 'La commission vendeur (DH) est requise';
    }
    if (!formData.mazidCommissionPct || parseFloat(formData.mazidCommissionPct) < 0) {
      errors.mazidCommissionPct = 'La commission Mazid (%) est requise';
    }

    if (formData.productIds.length === 0) {
      errors.productIds = 'Veuillez sélectionner au moins un produit';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission handler
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare promotion data for submission
      const promotionInput = {
        name: formData.name,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        quantity: parseInt(formData.quantity),
        // FIXED_PERCENTAGE: commissionAmount = seller DH, commissionPercentage = Mazid % of PGHT
        commissionType: 'FIXED_PERCENTAGE',
        laboratoryId: laboratory.id,
        status: 'DRAFT',
        commissionAmount: parseFloat(formData.sellerCommissionDH),
        commissionPercentage: parseFloat(formData.mazidCommissionPct),
        contratId: formData.contratId || undefined,
        pdfFile: formData.pdfFileName || undefined,
        productIds: formData.productIds,
        allowedUserTypes: formData.allowedUserTypes.length > 0 ? formData.allowedUserTypes : undefined,
      };
      
      if (promotion) {
        // Update existing promotion
        promotionInput.id = promotion.id;
        await updatePromotion({
          variables: {
            updatePromotionInput: promotionInput
          }
        });
      } else {
        // Create new promotion
        await createPromotion({
          variables: {
            createPromotionInput: promotionInput
          }
        });
      }
      
      // Success is handled in the mutation callbacks
    } catch (error) {
      console.error('Error saving promotion:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={promotion ? 'Modifier la promotion' : 'Créer une nouvelle promotion'}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      size="xl"
    >
      <div className="space-y-5">

        {/* Existing promotions — visibility panel */}
        {(() => {
          const existing = existingPromosData?.promotionsByLaboratory || [];
          const active = existing.filter(p => (!promotion || p.id !== promotion.id));
          if (active.length === 0) return null;
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Promotions existantes pour ce laboratoire
              </p>
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {active.map(p => {
                  const start = format(new Date(p.startDate), 'dd/MM/yyyy');
                  const end = format(new Date(p.endDate), 'dd/MM/yyyy');
                  const statusColors = {
                    IN_PROGRESS: 'bg-green-100 text-green-700',
                    DRAFT: 'bg-gray-100 text-gray-600',
                    CLOSED: 'bg-red-100 text-red-600',
                  };
                  const col = statusColors[p.status] || 'bg-gray-100 text-gray-600';
                  return (
                    <div key={p.id} className="flex items-center justify-between bg-white rounded px-2 py-1 text-xs border border-amber-100">
                      <span className="font-medium text-gray-800 truncate max-w-[40%]">{p.name}</span>
                      <span className="text-gray-500">{start} → {end}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${col}`}>{p.status}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Basic promotion details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la promotion <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom de la promotion"
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contrat associé
            </label>
            <select
              name="contratId"
              value={formData.contratId || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Sélectionner un contrat (optionnel)</option>
              {availableContracts.map(contract => (
                <option key={contract.id} value={contract.id}>
                  {contract.nom || `Contrat ${contract.number}`} ({format(new Date(contract.dateStart), 'dd/MM/yyyy')} - {format(new Date(contract.dateEnd), 'dd/MM/yyyy')})
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Associer cette promotion à un contrat existant
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.startDate ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.startDate && (
              <p className="mt-1 text-sm text-red-600">{formErrors.startDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.endDate ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.endDate && (
              <p className="mt-1 text-sm text-red-600">{formErrors.endDate}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantité de codes QR <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
              min="1"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.quantity ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="100"
            />
            {formErrors.quantity && (
              <p className="mt-1 text-sm text-red-600">{formErrors.quantity}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Nombre total de codes QR à générer pour cette promotion
            </p>
          </div>

          {/* Commissions Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Commission Vendeur */}
            <div className="bg-green-50 p-4 rounded-md border border-green-100">
              <h3 className="text-base font-medium text-green-800 mb-1">Gain Vendeur par scan</h3>
              <p className="text-xs text-gray-500 mb-3">Montant fixe en DH gagné par le vendeur à chaque scan</p>
              <div className="relative">
                <input
                  type="number"
                  name="sellerCommissionDH"
                  value={formData.sellerCommissionDH}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    formErrors.sellerCommissionDH ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="5.00"
                />
                <span className="absolute right-3 top-2 text-gray-500 text-sm">DH</span>
              </div>
              {formErrors.sellerCommissionDH && (
                <p className="mt-1 text-sm text-red-600">{formErrors.sellerCommissionDH}</p>
              )}
              {formData.sellerCommissionDH && (
                <p className="mt-2 text-sm font-semibold text-green-700">
                  = {parseFloat(formData.sellerCommissionDH).toFixed(2)} DH / scan
                </p>
              )}
            </div>

            {/* Commission Mazid */}
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
              <h3 className="text-base font-medium text-blue-800 mb-1">Gain Mazid par scan</h3>
              <p className="text-xs text-gray-500 mb-3">Pourcentage calculé sur le prix PGHT du produit</p>
              <div className="relative">
                <input
                  type="number"
                  name="mazidCommissionPct"
                  value={formData.mazidCommissionPct}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className={`w-full px-3 py-2 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.mazidCommissionPct ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="5"
                />
                <span className="absolute right-3 top-2 text-gray-500 text-sm">%</span>
              </div>
              {formErrors.mazidCommissionPct && (
                <p className="mt-1 text-sm text-red-600">{formErrors.mazidCommissionPct}</p>
              )}
              {getMazidGainDH() && (
                <p className="mt-2 text-sm font-semibold text-blue-700">
                  ≈ {getMazidGainDH()} DH / scan (moy. PGHT)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* User Type Restriction */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Types d'utilisateurs autorisés
          </label>
          <p className="text-xs text-gray-500 mb-2">Laisser vide = accessible à tous les types</p>
          <div className="flex flex-wrap gap-4">
            {USER_TYPES.map(ut => (
              <label key={ut.value} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  value={ut.value}
                  checked={formData.allowedUserTypes.includes(ut.value)}
                  onChange={handleUserTypeChange}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">{ut.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Produits associés <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3">
            {availableProducts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                Aucun produit disponible pour ce laboratoire. Veuillez d'abord ajouter des produits.
              </p>
            ) : (
              <div className="space-y-2">
                {availableProducts.map(product => (
                  <div key={product.id} className="flex items-center">
                    <input
                      id={`product-${product.id}`}
                      name="productIds"
                      type="checkbox"
                      value={product.id}
                      checked={formData.productIds.includes(product.id)}
                      onChange={handleProductChange}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`product-${product.id}`} className="ml-3 text-sm text-gray-700 flex items-center justify-between w-full">
                      <div className="flex items-center">
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-8 w-8 object-cover rounded-md mr-2"
                          />
                        )}
                        <span>{product.name}</span>
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        {product.prixPGHT ? `PGHT: ${product.prixPGHT.toFixed(2)} DH` : `Prix: ${product.price?.toFixed(2)} DH`}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
          {formErrors.productIds && (
            <p className="mt-1 text-sm text-red-600">{formErrors.productIds}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document PDF (optionnel)
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload-promotion"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-green-700 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                >
                  <span>Ajouter un fichier</span>
                  <input
                    id="file-upload-promotion"
                    name="pdfFile"
                    type="file"
                    accept=".pdf"
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">ou glisser-déposer</p>
              </div>
              <p className="text-xs text-gray-500">PDF uniquement</p>
              {formData.pdfFileName && (
                <p className="text-sm text-green-700">{formData.pdfFileName}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 p-3 bg-blue-50 rounded-md">
          <p className="text-blue-700 text-sm">
            <Info size={16} className="inline mr-1" />
            Cette promotion sera associée au laboratoire <strong>{laboratory?.nom}</strong>.
            {formData.contratId && (
              <> Elle sera également liée au contrat sélectionné.</>
            )}
          </p>
        </div>
      </div>
    </ModalComponent>
  );
};

export default PromotionForm;